import { useMemo } from 'react';
import { Badge, Col, Tabs, Table, Popover, Typography } from 'antd';
//import areas from './initStructure/SpartaAreas';
import topics from './initStructure/SpartaTopics';
import competencies from './initStructure/NiceCompetencies';
//import rolesData from './initStructure/NistWorkRoles';

const { Title } = Typography;

const Statistics = ({ columns, courses: coursesProp, rsSkillsGroup, rsProfiles }) => {
  const courses = useMemo(() => {
    const list = [];
    ['1-w', '1-s', '2-w', '2-s', '3-w', '3-s'].forEach((colId) => {
      const col = columns[colId];
      if (!col || !Array.isArray(col.courseIds)) return;
      col.courseIds.forEach((cid) => {
        const course = coursesProp[cid];
        if (course) list.push({ ...course, position: colId });
      });
    });
    return list;
  }, [columns, coursesProp]);

  const ectsSum = useMemo(() => {
    const sumObj = { '1-w': 0, '1-s': 0, '2-w': 0, '2-s': 0, '3-w': 0, '3-s': 0, sum: 0 };
    courses.forEach((c) => {
      sumObj[c.position] += c.credits;
      sumObj.sum += c.credits;
    });
    return sumObj;
  }, [courses]);

  const round = (value, prec = 0) => {
    const m = Math.pow(10, prec);
    return Math.round(value * m) / m;
  };

  const compIds = useMemo(() => {
    const ids = new Set();
    courses.forEach((c) => {
      c.topics.forEach((t) => {
        topics[t.topicId].comps.forEach((cid) => ids.add(cid));
      });
    });
    return Array.from(ids).sort((a, b) => a - b);
  }, [courses]);

  const ectsTopics = useMemo(() => {
    const tmp = JSON.parse(JSON.stringify(topics));
    courses.forEach((c) => {
      c.topics.forEach((t) => {
        const delta = t.ectsPer * c.credits;
        tmp[t.topicId].credits = (tmp[t.topicId].credits || 0) + delta;
      });
    });
    return tmp;
  }, [courses]);

  const niceCompetencies = useMemo(() => {
    const items = compIds.map((cid, i) => (
      <div className="work-role" key={i}>
        • {competencies[cid]}
      </div>
    ));
    return items.length ? items : <div className="empty-statistics-data">None</div>;
  }, [compIds]);

  const selectedSkillIds = useMemo(() => {
    const set = new Set();
    courses.forEach((c) => c.skills_group.forEach((sg) => set.add(sg)));
    return Array.from(set).sort((a, b) => a - b);
  }, [courses]);

  const enisaProfileCoverage = useMemo(() => {
    return rsProfiles.map((p) => {
      const included = [];
      const missing = [];
      p.skills_group.forEach((sg) => (selectedSkillIds.includes(sg) ? included.push(sg) : missing.push(sg)));
      const coverage = Math.round((included.length / p.skills_group.length) * 100);
      return { name: p.name, coverage, included, missing };
    });
  }, [rsProfiles, selectedSkillIds]);

  const percentageLevel = (pct) => (pct > 67 ? 'High' : pct > 33 ? 'Medium' : pct >= 0 ? 'Low' : 'Error');

  const ecsfData = enisaProfileCoverage.map((p, i) => ({ key: i, profile: p.name, coverage: percentageLevel(p.coverage), p }));
  const ecsfColumns = [
    { title: 'Profile Name', dataIndex: 'profile', key: 'profile' },
    {
      title: 'Coverage',
      dataIndex: 'coverage',
      key: 'coverage',
      align: 'center',
      onCell: (record) => ({
        className: record.p.coverage === 100 ? 'green' : 'normal',
      }),
      render: (text, record) => (
        <Popover
          placement="leftBottom"
          arrowPointAtCenter
          getPopupContainer={(trigger) => trigger.parentElement}
          trigger="hover"
          content={
            <div className="skills-popover">
              <Title level={4}>{record.p.name}</Title>
              <Title level={5}>Included Skills</Title>
              <ul>{record.p.included.length > 0 ? record.p.included.map((sg, i) => <li key={i}>{rsSkillsGroup.find((e) => e.id === sg).name}</li>) : <span className="empty">None</span>}</ul>
              <Title level={5}>Missing Skills</Title>
              <ul>{record.p.missing.length > 0 ? record.p.missing.map((sg, i) => <li key={i}>{rsSkillsGroup.find((e) => e.id === sg).name}</li>) : <span className="empty">None</span>}</ul>
            </div>
          }
        >
          <div className="coverage-text">
            {text}
          </div>
        </Popover>
      ),
    },
  ];

  const topicsData = Object.values(ectsTopics)
    .filter((t) => t.credits)
    .map((t, i) => ({ key: i, topic: t.name, ects: round((t.credits * 100) / ectsSum.sum, 1) + '%' }));
  const topicsColumns = [
    { title: 'Topic', dataIndex: 'topic', key: 'topic' },
    { title: 'ECTS', dataIndex: 'ects', key: 'ects' },
  ];

  const median = (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  };

  const medianCoverage = median(enisaProfileCoverage.map((p) => p.coverage));

  const items = [
    {
      key: 'ecsf',
      label: (
        <div className="ecsf-label">
          ECSF Framework <Badge count="Beta" style={{ backgroundColor: '#fafafa', color: '#000', boxShadow: '0 0 0 1px #d9d9d9' }} />
        </div>
      ),
      children: (
        <>
          <h6>ENISA Profiles coverage:</h6>
          <Table
            size="small"
            pagination={false}
            dataSource={ecsfData}
            columns={ecsfColumns}
            footer={() => (
              <div className="statistics-footer">
                <div className="result-text">Result (median): </div>
                <div className={medianCoverage === 100 ? 'green' : 'normal'}>{percentageLevel(medianCoverage)}</div>
              </div>
            )}
          />
        </>
      ),
    },
    {
      key: 'nist',
      label: 'NIST NICE Framework',
      children: (
        <>
          <h6>ECTS by SPARTA topics:</h6>
          {topicsData.length > 0 ? <Table size="small" pagination={false} dataSource={topicsData} columns={topicsColumns} /> : <div className="empty-statistics-data">None</div>}
          <h6>NICE Competencies:</h6>
          {niceCompetencies}
          <h6>Supported Work Roles according to NIST:</h6>
          <div className="empty-statistics-data"> None</div>
          {/* Render supported roles here */}
        </>
      ),
    },
  ];

  const ectsData = [
    { key: '1', year: '1st year', winter: ectsSum['1-w'], summer: ectsSum['1-s'], sum: ectsSum['1-w'] + ectsSum['1-s'] },
    { key: '2', year: '2nd year', winter: ectsSum['2-w'], summer: ectsSum['2-s'], sum: ectsSum['2-w'] + ectsSum['2-s'] },
    { key: '3', year: '3rd year', winter: ectsSum['3-w'], summer: ectsSum['3-s'], sum: ectsSum['3-w'] + ectsSum['3-s'] },
    { key: '4', year: 'Total', winter: '', summer: '', sum: ectsSum.sum },
  ];

  const ectsColumns = [
    { title: '', dataIndex: 'year', key: 'year', align: 'center' },
    { title: 'Winter', dataIndex: 'winter', key: 'winter', align: 'center' },
    { title: 'Summer', dataIndex: 'summer', key: 'summer', align: 'center' },
    { title: 'Sum', dataIndex: 'sum', key: 'sum', render: (text) => <div className="sum-text">{text}</div>, align: 'center' },
  ];

  return (
    <Col xs={24} lg={24} xl={8} className="base-statistics">
      <h3 className="section-title">Statistics</h3>
      <div className="base-statistics-content">
        {courses.length > 0 ? (
          <div className="base-statistics-content-scroll">
            <div className="statistics-results">
              <h6>Total ECTS credits:</h6>
              <Table size="small" pagination={false} dataSource={ectsData} columns={ectsColumns} showHeader={true} />
              <Tabs defaultActiveKey="ecsf" size="small" items={items} />¨
            </div>
          </div>
        ) : (
          <div className="empty-data">
            <span className="header">There are no courses yet.</span>
            <br />
            Drag some course to your curricula.
          </div>
        )}
      </div>
    </Col>
  );
};

export default Statistics;
